<template>
  <div
    v-if="currentFacePaperUrl && isAvailable && !hasUploadedArtwork"
    class="frame-only"
  >
    <div class="frame-only__divider">
      <span>or</span>
    </div>
    <p class="frame-only__subtext">
      Already have a{{ artworkOpeningSize ? ` ${artworkOpeningSize}` : '' }} photo? Buy the frame and add it later
    </p>
    <button
      ref="addToCartButton"
      class="frame-only__btn btn btn--secondary btn--full"
      type="button"
      :disabled="isProcessing"
      @click="addToCart"
    >
      {{ addToCartLabel }}
    </button>
  </div>

  <div v-if="previewImageUrl">
    <Teleport to="#bagContent">
      <div class="bag-added__image">
        <div class="bag-added__image-wrapper">
          <img :src="previewImageUrl" alt="Frame Preview">
        </div>
        <div class="bag-added__image-label">
          <span class="bag-added__image-label-text">Item Added!</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Teleport } from 'vue';
import GlazeClient from '@framebridge/toolbox/apihelpers/glazeClient';
import artworkUploader from '@framebridge/toolbox/apihelpers/artworkUploader';
import { normalizedArtworkData } from '@framebridge/toolbox/framingUtilities';
import FrameSpec from '@framebridge/toolbox/FrameSpec';
import elevarTracking from '../../lib/elevar-tracking';

export default {
  name: 'FrameOnlyAddToCart',
  components: {
    Teleport,
  },
  props: {
    // Strings, not numbers: FrameSpec matches catalog variants by string id
    productId: {
      type: String,
      required: true,
    },
    variantId: {
      type: String,
      required: true,
    },
    facePaperUrl: {
      type: String,
      default: '',
    },
    available: {
      type: Boolean,
      default: true,
    },
    price: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      joineryHost: SDG?.Data?.joineryHost || 'https://staging.framebridge.com',
      shopifyHost: SDG?.Data?.shopifyHost || 'https://staging.framebridge.dev',
      cartHost: SDG?.Data?.cartHost || 'https://underpass.staging.framebridge.io',
      currentVariantId: this.variantId,
      currentFacePaperUrl: this.facePaperUrl,
      isAvailable: this.available,
      hasUploadedArtwork: false,
      artworkOpeningsByVariantId: null,
      currentPrice: this.price,
      isProcessing: false,
      previewImageUrl: '',
    };
  },
  computed: {
    addToCartLabel() {
      return this.currentPrice
        ? `Add To Cart - Frame Only - ${this.currentPrice}`
        : 'Add To Cart - Frame Only';
    },
    artworkOpening() {
      return this.artworkOpeningsByVariantId?.[this.currentVariantId];
    },
    artworkOpeningSize() {
      const width = this.formatInches(this.artworkOpening?.widthInInches);
      const height = this.formatInches(this.artworkOpening?.heightInInches);

      return width && height ? `${width}" × ${height}"` : '';
    },
  },
  mounted() {
    this.checkUploadedArtwork();
    this.fetchArtworkOpenings();

    window.addEventListener('frameOnlyVariantChanged', this.handleVariantChange);
  },
  methods: {
    checkUploadedArtwork() {
      try {
        const stored = localStorage.getItem('uploadedArtwork');
        const parsed = stored && JSON.parse(stored);

        this.hasUploadedArtwork = !!parsed?.artwork;
      } catch (error) {
        console.error({ message: 'Error reading uploadedArtwork from localStorage', error });
        this.hasUploadedArtwork = false;
      }
    },
    formatInches(value) {
      if (!value && value !== 0) return null;
      return `${Math.round(value * 100) / 100}`;
    },
    async fetchArtworkOpenings() {
      if (this.artworkOpeningsByVariantId || !this.currentFacePaperUrl) return;

      try {
        const client = new GlazeClient(null, { rejectIfNotOk: true, baseUrl: this.joineryHost });
        const response = await client.get(`/api/v1/shopify_product_details/${this.productId}`);

        this.artworkOpeningsByVariantId = response.data.variants.reduce((openings, variant) => {
          openings[variant.shopifyVariantId] = variant.frameSpec?.artworks?.[0]?.exterior;
          return openings;
        }, {});
      } catch (error) {
        console.error({ message: 'Error fetching artwork openings', error });
        this.artworkOpeningsByVariantId = {};
      }
    },
    handleVariantChange(event) {
      const variant = event?.detail;

      if (!variant) return;

      this.currentVariantId = String(variant.variantId);
      this.currentFacePaperUrl = variant.facePaperUrl;
      this.isAvailable = variant.available;
      this.currentPrice = variant.price;
      this.checkUploadedArtwork();
      this.fetchArtworkOpenings();
    },
    async addToCart() {
      if (!this.currentFacePaperUrl || !this.isAvailable || this.hasUploadedArtwork || this.isProcessing) return;

      this.isProcessing = true;
      const button = this.$refs.addToCartButton;
      if (button) {
        button.classList.add('is-loading');
      }

      try {
        await this.fetchArtworkOpenings();

        const client = new GlazeClient(null, { rejectIfNotOk: true, baseUrl: this.joineryHost });
        const response = await artworkUploader({
          url: this.currentFacePaperUrl,
          client,
          artwork: { sample_art: true },
        });
        const artwork = {
          ...normalizedArtworkData(response.data),
          conveyance: 'uploaded',
          exterior: this.artworkOpening,
          offset: { topInInches: 'auto', leftInInches: 'auto' },
        };

        const spec = await FrameSpec.build({
          query: { product: this.productId, variant: this.currentVariantId },
          joineryHost: this.joineryHost,
          shopifyHost: this.shopifyHost,
          artwork,
        });

        const cartResponse = await spec.addToCart({ cartHost: this.cartHost });

        const { product, variant } = spec;
        elevarTracking({
          sku: variant?.sku,
          product_title: product?.title,
          vendor: 'Framebridge',
          product_type: product?.productType,
          variant_title: variant?.title,
          price: variant?.price,
          quantity: 1,
          product_id: this.productId,
          variant_id: this.currentVariantId,
          image: product?.media?.[0]?.image?.url || '',
          inventory_quantity: variant?.inventoryQuantity,
        }, 'add');

        if (!SDG.Data.enableItemAddedDrawer) {
          window.location.href = '/cart';
          return;
        }

        this.previewImageUrl = spec.previewImage?.url || '';

        SDG.Bag.refresh(await cartResponse.json(), this.currentVariantId);
      } catch (error) {
        console.error({ message: 'Error adding frame-only item to cart', error });
      }

      this.isProcessing = false;
      if (button) {
        button.classList.remove('is-loading');
      }
    },
  },
};
</script>

<style lang="scss">
.frame-only {
  &__divider {
    align-items: center;
    display: flex;
    gap: 1rem;
    margin: 1.5rem 0;

    &::before,
    &::after {
      background-color: #a8a8a8;
      content: '';
      flex: 1;
      height: 1px;
    }

    span {
      color: #767676;
    }
  }

  &__subtext {
    color: #505050;
    font-size: 1rem;
    letter-spacing: 0.32px;
    line-height: 1.625rem;
    margin-bottom: 1rem;
    text-align: center;
  }
}
</style>
